// [2024-05-08] 환자 목록을 가져오는 미들웨어 구현
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Patient, AIReview, Prescription, PrescriptionGroup, ConsultationRecordListItem } from "../common/models/types";
import { setPatientList, setPatient } from "./actions";
import {
  fetchPatients,
  fetchPatientById as fetchPatientByIdApi,
  fetchPatientPrescriptions as fetchPatientPrescriptionsApi,
} from "../api/patientService";
import {
  fetchPatientAnalysisHistory,
  fetchAnalysisResult,
  fetchAnalysisHistory,
  fetchPinnedReviews as fetchPinnedReviewsApi,
  fetchAiReviewCount as fetchAiReviewCountApi,
  AiReviewCountParams,
} from "../api/aiReviewService";
import { fetchRecentNhisRequests as fetchRecentNhisRequestsApi } from "../api/nhisService";
import { RootState } from "./index";
import { groupPrescriptions } from "../utils/PrescriptionUtils";
import { log, LogLevel } from "utils/LogUtil";
import { checkDrugInfo } from "api/drugService";
import { fetchConsultationRecords } from "api/consultationService";
import { fetchUserDrugIdentificationList } from "api/drugIdentificationService";

export interface FetchPatientListOptions {
  forceRefresh?: boolean;
  includeNhisStatus?: boolean;
}

// 환자 목록을 가져오는 thunk 액션
export const fetchPatientList = createAsyncThunk(
  "patients/fetchList",
  async (options: FetchPatientListOptions = {}, { getState, dispatch }) => {
    const { forceRefresh = false, includeNhisStatus = false } = options;
    log(LogLevel.VERBOSE, "[middleware] fetchPatientList 시작:", {
      forceRefresh,
      includeNhisStatus,
      timestamp: new Date().toISOString(),
    });

    const state = getState() as RootState;
    log(LogLevel.VERBOSE, "[middleware] 현재 상태:", {
      hasPatientList: !!state.data?.patientList,
      patientListLength: state.data?.patientList?.length,
      timestamp: new Date().toISOString(),
    });

    // patientList가 null이 아니고 forceRefresh가 false면 API 호출하지 않음
    if (state.data?.patientList !== null && !forceRefresh) {
      log(LogLevel.VERBOSE, "[middleware] 기존 환자 목록 사용:", {
        patientCount: state.data?.patientList?.length || 0,
        timestamp: new Date().toISOString(),
      });
      return state.data?.patientList;
    }

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchPatients");
      const response = await fetchPatients({ includeNhisStatus });
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        dataLength: Array.isArray(response.data) ? response.data.length : 0,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자 목록을 가져오는데 실패했습니다.");
      }

      if (!response.data || !Array.isArray(response.data)) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 형식 오류:", {
          data: response.data,
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자 목록 데이터 형식이 올바르지 않습니다.");
      }

      const patients = response.data as Patient[];
      log(LogLevel.VERBOSE, "[middleware] 환자 데이터 처리 시작:", {
        patientCount: patients.length,
        timestamp: new Date().toISOString(),
      });

      // 환자 목록 저장 (id만 포함)
      const patientList = patients.map((patient) => ({ id: patient.id }));
      log(
        LogLevel.VERBOSE,
        "[middleware] 환자 ID 목록 저장:",
        {
          patientCount: patientList.length,
          timestamp: new Date().toISOString(),
        },
        patientList
      );
      dispatch(setPatientList(patientList));

      // 각 환자의 상세 정보를 개별적으로 저장
      log(LogLevel.VERBOSE, "[middleware] 환자 상세 정보 저장 시작");
      patients.forEach((patient, index) => {
        log(LogLevel.VERBOSE, `[middleware] 환자 ${index + 1}/${patients.length} 저장:`, {
          patientId: patient.id,
          timestamp: new Date().toISOString(),
        });
        dispatch(setPatient(patient));
      });

      log(LogLevel.VERBOSE, "[middleware] fetchPatientList 완료:", {
        totalPatients: patients.length,
        timestamp: new Date().toISOString(),
      });
      return patients;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 목록 조회 실패:", {
        error,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 AI Review 목록을 가져오는 thunk 액션
export const fetchPatientReviews = createAsyncThunk(
  "patients/fetchReviews",
  async (patientId: number, { dispatch }) => {
    try {
      const response = await fetchPatientAnalysisHistory(patientId);

      if (response.error) {
        throw new Error(response.message || "환자의 AI Review 목록을 가져오는데 실패했습니다.");
      }

      const reviews = response.data as AIReview[];

      // 각 Review의 상세 정보를 개별적으로 저장
      reviews.forEach((review) => {
        dispatch({
          type: "SET_REVIEW",
          payload: review,
        });
      });

      // patient에 reviews 배열 저장
      dispatch({
        type: "SET_PATIENT_REVIEWS",
        payload: {
          patientId,
          reviews,
        },
      });

      return reviews;
    } catch (error) {
      log(LogLevel.ERROR, "환자 AI Review 목록 조회 실패:", error);
      throw error;
    }
  }
);

// 환자의 AI Review 목록을 가져오는 thunk 액션
export const fetchCheckReference = createAsyncThunk(
  "medicine/fetchCheckReference",
  async (itemCode: string, { getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const referenceChecker = state.data?.referenceChecker;

      if (referenceChecker[itemCode] !== undefined) {
        return referenceChecker[itemCode];
      }

      dispatch({
        type: "SET_REFERENCE_CHECKER",
        payload: {
          itemCode,
          count: -1,
          totalLength: 0,
        },
      });

      const response = await checkDrugInfo(itemCode);

      if (response.error) {
        dispatch({
          type: "SET_REFERENCE_CHECKER",
          payload: {
            itemCode,
            count: 0,
            totalLength: 0,
            totalBytes: 0,
          },
        });
        throw new Error(response.message || "환자의 AI Review 목록을 가져오는데 실패했습니다.");
      }

      dispatch({
        type: "SET_REFERENCE_CHECKER",
        payload: {
          itemCode,
          count: response.data.count || response.data,
          totalLength: response.data.totalLength || 0,
          totalBytes: response.data.totalBytes || 0,
        },
      });

      return response.data;
    } catch (error) {
      log(LogLevel.ERROR, "환자 AI Review 목록 조회 실패:", error);
      throw error;
    }
  }
);

// 여러 약품 정보를 벌크 조회 후 미크롤링 항목만 순차 크롤링하는 thunk 액션
export const fetchCheckReferenceBatch = createAsyncThunk(
  "medicine/fetchCheckReferenceBatch",
  async (itemCodes: string[], { getState, dispatch }) => {
    const state = getState() as RootState;
    const referenceChecker = state.data?.referenceChecker || {};

    if (itemCodes.length === 0) {
      return { processed: 0 };
    }

    log(LogLevel.INFO, `[fetchCheckReferenceBatch] 처리할 itemCodes: ${itemCodes.length}개`);

    try {
      // 1단계: 벌크 조회
      log(LogLevel.INFO, `[fetchCheckReferenceBatch] 벌크 조회 시작`);
      const { checkDrugsBulk } = await import("api/drugService");
      const bulkResults = await checkDrugsBulk(itemCodes);

      // 벌크 조회 결과를 Redux에 즉시 반영
      Object.entries(bulkResults).forEach(([itemCode, count]) => {
        dispatch({
          type: "SET_REFERENCE_CHECKER",
          payload: {
            itemCode,
            count,
            totalLength: 0,  // 더 이상 사용하지 않음
            totalBytes: 0,   // 더 이상 사용하지 않음
          }
        });
      });

      // 2단계: 미크롤링 항목 추출
      const uncrawledItems = Object.entries(bulkResults)
        .filter(([_, count]) => count === -1)
        .map(([itemCode]) => itemCode);

      log(LogLevel.INFO, `[fetchCheckReferenceBatch] 미크롤링 항목: ${uncrawledItems.length}개`);

      // 3단계: 미크롤링 항목 개별 크롤링 (순차 처리 - 서버 부하 방지)
      const crawlResults = [];
      if (uncrawledItems.length > 0) {
        for (const itemCode of uncrawledItems) {
          try {
            const response = await checkDrugInfo(itemCode);
            const count = response.error ? 0 : (response.data.count || response.data);

            // Progressive Loading: 개별 크롤링 완료 시마다 Redux 업데이트
            dispatch({
              type: "SET_REFERENCE_CHECKER",
              payload: {
                itemCode,
                count,
                totalLength: 0,
                totalBytes: 0,
              }
            });

            crawlResults.push({ itemCode, count, success: !response.error });

            // 서버 부하 방지를 위한 지연 (200ms)
            if (uncrawledItems.indexOf(itemCode) < uncrawledItems.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } catch (error) {
            log(LogLevel.ERROR, `크롤링 실패: ${itemCode}`, error);
            dispatch({
              type: "SET_REFERENCE_CHECKER",
              payload: { itemCode, count: 0, totalLength: 0, totalBytes: 0 }
            });
            crawlResults.push({ itemCode, count: 0, success: false });
          }
        }
      }

      return {
        processed: itemCodes.length,
        results: Object.entries(bulkResults).map(([itemCode, count]) => ({
          itemCode,
          count,
          totalLength: 0,
          totalBytes: 0,
          success: count >= 0,
        }))
      };

    } catch (error) {
      log(LogLevel.ERROR, `[fetchCheckReferenceBatch] 벌크 조회 실패:`, error);

      // Fallback: 기존 순차 처리 방식으로 되돌림
      const results = [];
      for (const itemCode of itemCodes) {
        try {
          const response = await checkDrugInfo(itemCode);
          const count = response.error ? 0 : (response.data.count || response.data);

          dispatch({
            type: "SET_REFERENCE_CHECKER",
            payload: { itemCode, count, totalLength: 0, totalBytes: 0 }
          });

          results.push({ itemCode, count, totalLength: 0, totalBytes: 0, success: !response.error });

          // 서버 부하 방지를 위한 지연
          await new Promise(resolve => setTimeout(resolve, 200));

        } catch (err) {
          log(LogLevel.ERROR, `약품 정보 확인 실패 (${itemCode}):`, err);
          dispatch({
            type: "SET_REFERENCE_CHECKER",
            payload: { itemCode, count: 0, totalLength: 0, totalBytes: 0 }
          });
          results.push({ itemCode, count: 0, totalLength: 0, totalBytes: 0, success: false });
        }
      }

      return { processed: results.length, results };
    }
  }
);

// 한 개의 AI Review 상세 정보를 가져오는 thunk 액션
export const fetchPatientReview = createAsyncThunk(
  "reviews/fetchById",
  async (reviewId: number, { dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientReview 시작:", {
      reviewId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchAnalysisResult");
      // reviewId를 문자열로 변환하여 API 호출
      const response = await fetchAnalysisResult(String(reviewId));
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "AI Review 정보를 가져오는데 실패했습니다.");
      }

      if (!response.data) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 없음:", {
          timestamp: new Date().toISOString(),
        });
        throw new Error("AI Review 정보가 존재하지 않습니다.");
      }

      const review = response.data as AIReview;
      log(LogLevel.VERBOSE, "[middleware] AI Review 데이터 처리:", {
        reviewId: review.id,
        timestamp: new Date().toISOString(),
      });

      // AI Review 정보 저장
      dispatch({
        type: "SET_REVIEW",
        payload: review,
      });

      log(LogLevel.VERBOSE, "[middleware] fetchPatientReview 완료:", {
        reviewId: review.id,
        timestamp: new Date().toISOString(),
      });
      return review;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] AI Review 정보 조회 실패:", {
        error,
        reviewId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 단일 환자 정보를 가져오는 thunk 액션
export const fetchPatientById = createAsyncThunk(
  "patients/fetchById",
  async (patientId: number, { dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientById 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchPatientById");
      const response = await fetchPatientByIdApi(patientId);
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자 정보를 가져오는데 실패했습니다.");
      }

      if (!response.data) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 없음:", {
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자 정보가 존재하지 않습니다.");
      }

      const patient = response.data as Patient;
      log(LogLevel.VERBOSE, "[middleware] 환자 데이터 처리:", {
        patientId: patient.id,
        timestamp: new Date().toISOString(),
      });

      // 환자 정보 저장
      dispatch(setPatient(patient));

      log(LogLevel.VERBOSE, "[middleware] fetchPatientById 완료:", {
        patientId: patient.id,
        timestamp: new Date().toISOString(),
      });
      return patient;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 정보 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 처방전 정보를 가져오는 thunk 액션
export const fetchPatientPrescriptions = createAsyncThunk(
  "patients/fetchPrescriptions",
  async (patientId: number, { getState, dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientPrescriptions 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchPatientPrescriptions");
      const response = await fetchPatientPrescriptionsApi(patientId);
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자의 처방전 정보를 가져오는데 실패했습니다.");
      }

      if (!response.data || !Array.isArray(response.data)) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 없음:", {
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자의 처방전 정보가 존재하지 않습니다.");
      }

      const prescriptions = response.data as Prescription[];
      log(LogLevel.VERBOSE, "[middleware] 처방전 데이터 처리:", {
        patientId,
        prescriptionCount: prescriptions.length,
        timestamp: new Date().toISOString(),
      });

      // PrescriptionUtils의 groupPrescriptions 함수를 사용하여 처방전 그룹화
      const prescriptionGroups = groupPrescriptions(prescriptions);

      // 현재 환자 정보 가져오기
      const state = getState() as RootState;
      const currentPatient = state.data?.patients?.[patientId];

      if (currentPatient) {
        // 환자 정보 업데이트
        const updatedPatient: Patient = {
          ...currentPatient,
          prescriptions,
          prescriptionGroups,
        };

        // 업데이트된 환자 정보 저장
        dispatch(setPatient(updatedPatient));
      }

      log(LogLevel.VERBOSE, "[middleware] fetchPatientPrescriptions 완료:", {
        patientId,
        prescriptionCount: prescriptions.length,
        groupCount: prescriptionGroups.length,
        timestamp: new Date().toISOString(),
      });

      return {
        prescriptions,
        prescriptionGroups,
      };
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 처방전 정보 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 OTC 정보를 가져오는 thunk 액션
export const fetchPatientOTC = createAsyncThunk(
  "patients/fetchOTC",
  async (patientId: number, { getState, dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientOTC 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchPatientOTC");
      const response = await fetchPatientPrescriptionsApi(patientId, "otc");
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자의 OTC 정보를 가져오는데 실패했습니다.");
      }

      if (!response.data || !Array.isArray(response.data)) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 없음:", {
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자의 OTC 정보가 존재하지 않습니다.");
      }

      const otc = response.data as Prescription[];
      log(LogLevel.VERBOSE, "[middleware] OTC 데이터 처리:", {
        patientId,
        otcCount: otc.length,
        timestamp: new Date().toISOString(),
      });

      // 현재 환자 정보 가져오기
      const state = getState() as RootState;
      const currentPatient = state.data?.patients?.[patientId];

      if (currentPatient) {
        // 환자 정보 업데이트
        const updatedPatient: Patient = {
          ...currentPatient,
          otc,
        };

        // 업데이트된 환자 정보 저장
        dispatch(setPatient(updatedPatient));
      }

      log(LogLevel.VERBOSE, "[middleware] fetchPatientOTC 완료:", {
        patientId,
        otcCount: otc.length,
        timestamp: new Date().toISOString(),
      });

      return otc;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 OTC 정보 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 HFF 정보를 가져오는 thunk 액션
export const fetchPatientHFF = createAsyncThunk(
  "patients/fetchHFF",
  async (patientId: number, { getState, dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientHFF 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchPatientHFF");
      const response = await fetchPatientPrescriptionsApi(patientId, "hff");
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자의 HFF 정보를 가져오는데 실패했습니다.");
      }

      if (!response.data || !Array.isArray(response.data)) {
        log(LogLevel.ERROR, "[middleware] API 응답 데이터 없음:", {
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자의 HFF 정보가 존재하지 않습니다.");
      }

      const hff = response.data as Prescription[];
      log(LogLevel.VERBOSE, "[middleware] HFF 데이터 처리:", {
        patientId,
        hffCount: hff.length,
        timestamp: new Date().toISOString(),
      });

      // 현재 환자 정보 가져오기
      const state = getState() as RootState;
      const currentPatient = state.data?.patients?.[patientId];

      if (currentPatient) {
        // 환자 정보 업데이트
        const updatedPatient: Patient = {
          ...currentPatient,
          hff,
        };

        // 업데이트된 환자 정보 저장
        dispatch(setPatient(updatedPatient));
      }

      log(LogLevel.VERBOSE, "[middleware] fetchPatientHFF 완료:", {
        patientId,
        hffCount: hff.length,
        timestamp: new Date().toISOString(),
      });

      return hff;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 HFF 정보 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 상담 기록 목록을 가져오는 thunk 액션
export const fetchPatientConsultationRecords = createAsyncThunk(
  "patients/fetchConsultationRecords",
  async (patientId: number, { getState, dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientConsultationRecords 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      log(LogLevel.VERBOSE, "[middleware] API 호출 시작: fetchConsultationRecords");
      const response = await fetchConsultationRecords({ patientId, status: 'completed' });
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        errorMessage: response.message,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          message: response.message,
          timestamp: new Date().toISOString(),
        });
        throw new Error(response.message || "환자의 상담 기록을 가져오는데 실패했습니다.");
      }

      const records: ConsultationRecordListItem[] = response.data?.records || [];
      log(LogLevel.VERBOSE, "[middleware] 상담 기록 데이터 처리:", {
        patientId,
        recordCount: records.length,
        timestamp: new Date().toISOString(),
      });

      // 현재 환자 정보 가져오기
      const state = getState() as RootState;
      const currentPatient = state.data?.patients?.[patientId];

      if (currentPatient) {
        // 환자 정보 업데이트
        const updatedPatient: Patient = {
          ...currentPatient,
          consultationRecords: records,
        };

        // 업데이트된 환자 정보 저장
        dispatch(setPatient(updatedPatient));
      }

      log(LogLevel.VERBOSE, "[middleware] fetchPatientConsultationRecords 완료:", {
        patientId,
        recordCount: records.length,
        timestamp: new Date().toISOString(),
      });

      return records;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 상담 기록 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 환자의 식별 의약품 목록을 가져오는 thunk 액션
export const fetchPatientDrugIdentifications = createAsyncThunk(
  "patients/fetchDrugIdentifications",
  async (patientId: number, { getState, dispatch }) => {
    log(LogLevel.VERBOSE, "[middleware] fetchPatientDrugIdentifications 시작:", {
      patientId,
      timestamp: new Date().toISOString(),
    });

    try {
      const response = await fetchUserDrugIdentificationList({ patientId });
      log(LogLevel.VERBOSE, "[middleware] API 응답:", {
        hasError: !!response.error,
        hasData: !!response.data,
        timestamp: new Date().toISOString(),
      });

      if (response.error) {
        log(LogLevel.ERROR, "[middleware] API 응답 에러:", {
          error: response.error,
          timestamp: new Date().toISOString(),
        });
        throw new Error("환자의 식별 의약품 정보를 가져오는데 실패했습니다.");
      }

      const drugIdentifications = response.data || [];

      // 현재 환자 정보 가져오기
      const state = getState() as RootState;
      const currentPatient = state.data?.patients?.[patientId];

      if (currentPatient) {
        const updatedPatient: Patient = {
          ...currentPatient,
          drugIdentifications,
        };

        dispatch(setPatient(updatedPatient));
      }

      log(LogLevel.VERBOSE, "[middleware] fetchPatientDrugIdentifications 완료:", {
        patientId,
        count: drugIdentifications.length,
        timestamp: new Date().toISOString(),
      });

      return drugIdentifications;
    } catch (error) {
      log(LogLevel.ERROR, "[middleware] 환자 식별 의약품 조회 실패:", {
        error,
        patientId,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
);

// 최근 2주간의 AI Review 목록을 가져오는 thunk 액션
export const fetchReviewHistory = createAsyncThunk(
  "reviews/fetchHistory",
  async (_, { dispatch }) => {
    try {
      const response = await fetchAnalysisHistory();

      if (response.error) {
        throw new Error(response.message || "환자의 AI Review 목록을 가져오는데 실패했습니다.");
      }

      const reviews = response.data as AIReview[];

      // patient에 reviews 배열 저장
      dispatch({
        type: "SET_LATEST_REVIEWS",
        payload: reviews,
      });

      return reviews;
    } catch (error) {
      log(LogLevel.ERROR, "환자 AI Review 목록 조회 실패:", error);
      throw error;
    }
  }
);

// 핀 보고서 목록을 가져오는 thunk 액션 (대시보드 전용)
export const fetchPinnedReviews = createAsyncThunk(
  "reviews/fetchPinned",
  async (_, { dispatch }) => {
    try {
      const response = await fetchPinnedReviewsApi();

      if (response.error) {
        throw new Error(response.message || "핀 보고서를 가져오는데 실패했습니다.");
      }

      const reviews = (response.data ?? []) as AIReview[];

      dispatch({
        type: "SET_PINNED_REVIEWS",
        payload: reviews,
      });

      return reviews;
    } catch (error) {
      log(LogLevel.ERROR, "핀 보고서 조회 실패:", error);
      throw error;
    }
  }
);

// AI 리뷰 전체 건수를 가져오는 thunk 액션 (대시보드 통계 카드)
export const fetchAiReviewCount = createAsyncThunk(
  "reviews/fetchCount",
  async (params: AiReviewCountParams | undefined, { dispatch }) => {
    try {
      const response = await fetchAiReviewCountApi(params);

      if (response.error) {
        throw new Error(response.message || "AI 리뷰 건수를 가져오는데 실패했습니다.");
      }

      const total = response.data?.total ?? 0;

      dispatch({
        type: "SET_AI_REVIEW_COUNT",
        payload: total,
      });

      return total;
    } catch (error) {
      log(LogLevel.ERROR, "AI 리뷰 건수 조회 실패:", error);
      throw error;
    }
  }
);

// 핀 보고서 전체 건수를 가져오는 thunk 액션 (대시보드 핀 섹션 "총 N건" 표시)
export const fetchPinnedReviewCount = createAsyncThunk(
  "reviews/fetchPinnedCount",
  async (_, { dispatch }) => {
    try {
      const response = await fetchAiReviewCountApi({ pinnedOnly: true });

      if (response.error) {
        throw new Error(response.message || "핀 보고서 건수를 가져오는데 실패했습니다.");
      }

      const total = response.data?.total ?? 0;

      dispatch({
        type: "SET_PINNED_REVIEW_COUNT",
        payload: total,
      });

      return total;
    } catch (error) {
      log(LogLevel.ERROR, "핀 보고서 건수 조회 실패:", error);
      throw error;
    }
  }
);

// 최근 2일치 NHIS 요청 목록을 가져오는 thunk 액션 (대시보드 처방내역 조회 현황)
export const fetchRecentNhisRequests = createAsyncThunk(
  "nhis/fetchRecent",
  async (daysAgo: number = 2, { dispatch }) => {
    try {
      const response = await fetchRecentNhisRequestsApi(daysAgo);

      if (response.error) {
        throw new Error(response.message || "최근 NHIS 요청을 가져오는데 실패했습니다.");
      }

      const requests = response.data ?? [];

      dispatch({
        type: "SET_NHIS_RECENT_REQUESTS",
        payload: requests,
      });

      return requests;
    } catch (error) {
      log(LogLevel.ERROR, "최근 NHIS 요청 조회 실패:", error);
      throw error;
    }
  }
);
